<template>
  <div class="edit-work-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>编辑工作单</span>
      </div>

      <el-form
        ref="form"
        :model="editWorkInfo"
        label-width="120px"
        label-position="right"
      >
        <!-- 标题行：工作名称 + 工作类型（替换为级联选择器） -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              label="工作名称"
              prop="workName"
              :rules="[
                {
                  required: true,
                  message: '请输入工作名称',
                  trigger: 'blur',
                },
              ]"
            >
              <el-input
                v-model.trim="editWorkInfo.workName"
                placeholder="请输入工作名称"
                maxlength="20"
                clearable
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              label="工作类型"
              prop="wtIds"
              :rules="[
                {
                  required: true,
                  message: '请选择工作类型',
                  trigger: 'change',
                },
              ]"
            >
              <!-- 替换为级联选择器 -->
              <el-cascader
                v-model="editWorkInfo.wtIds"
                :options="workTypeOptions"
                :props="cascaderProps"
                placeholder="请选择工作类型"
                style="width: 100%"
                clearable
                @change="handleCascaderChange"
                :show-all-levels="true"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 处理人+抄送人（优化标签渲染key） -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              label="处理人"
              :rules="[
                {
                  required: true,
                  message: '请选择处理人',
                  trigger: 'change',
                },
              ]"
            >
              <div class="staff-tags">
                <el-tag
                  v-for="(staff, index) in editWorkInfo.staffs"
                  :key="`processor-${staff.staffId}-${staff.communityId}-${index}`"
                  closable
                  @close="_deleteWorkStaff(staff)"
                >
                  {{ staff.staffName }}
                  <span style="font-size: 12px; color: #999; margin-left: 5px">
                    ({{ staff.communityName || staff.communityId }})
                  </span>
                </el-tag>
                <el-button type="text" @click="openParkSelect('processor')">
                  <i class="el-icon-search"></i>选择
                </el-button>
              </div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="抄送人">
              <div class="staff-tags">
                <el-tag
                  v-for="(staff, index) in editWorkInfo.copyStaffs"
                  :key="`cc-${staff.staffId}-${staff.communityId}-${index}`"
                  closable
                  @close="_deleteCopyWorkStaff(staff)"
                >
                  {{ staff.staffName }}
                  <span style="font-size: 12px; color: #999; margin-left: 5px">
                    ({{ staff.communityName || staff.communityId }})
                  </span>
                </el-tag>
                <el-button type="text" @click="openParkSelect('cc')">
                  <i class="el-icon-search"></i>选择
                </el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 工作周期+附件（核心修改：仅显示文件名+后缀+移除功能） -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item
              label="工作周期"
              prop="workCycle"
              :rules="[
                {
                  required: true,
                  message: '请选择工作周期',
                  trigger: 'change',
                },
              ]"
            >
              <el-select
                v-model="editWorkInfo.workCycle"
                placeholder="请选择工作周期"
                style="width: 100%"
                @change="handleWorkCycleChange"
              >
                <el-option label="一次性工单" value="1001" />
                <el-option label="周期性工单" value="2002" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item
              label="紧急程度"
              prop="urgencyLevel"
              :rules="[
                {
                  required: true,
                  message: '请选择紧急程度',
                  trigger: 'change',
                },
              ]"
            >
              <el-select
                v-model="editWorkInfo.urgencyLevel"
                placeholder="请选择紧急程度"
                style="width: 100%"
                @change="handleWorkCycleChange"
              >
                <el-option label="重点" value="1001" />
                <el-option label="紧急" value="1002" />
                <el-option label="普通" value="1003" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row>
          <el-col :span="12">
            <el-form-item label="附件">
              <!-- 核心修改：仅显示文件名+后缀 + 移除按钮 -->
              <div class="file-info" v-if="editWorkInfo.pathUrl">
                <el-tag type="info">
                  {{ getFileNameFromUrl(editWorkInfo.pathUrl) }}
                </el-tag>
                <el-button
                  type="text"
                  icon="el-icon-delete"
                  style="margin-left: 10px"
                  @click="removeFile"
                >
                  移除
                </el-button>
              </div>
              <!-- 替换为上传按钮，点击打开弹窗 -->
              <el-button
                type="primary"
                icon="el-icon-upload"
                @click="openUploadModal"
                v-if="!editWorkInfo.pathUrl"
              >
                上传附件
              </el-button>
              <el-button
                type="primary"
                icon="el-icon-upload"
                @click="openUploadModal"
                v-else
              >
                替换附件
              </el-button>
            </el-form-item>
          </el-col>
        </el-row>
        <!-- 核心：多组内容项（时间+内容） -->
        <el-row
          v-for="(item, index) in editWorkInfo.contents"
          :key="item.id"
          class="work-content-group"
        >
          <!-- 时间选择 -->
          <el-row :gutter="20" class="content-operate-row">
            <el-col :span="8">
              <el-form-item
                :label="`时间${index + 1}`"
                :prop="`contents[${index}].periodTime`"
                :rules="[
                  {
                    required: true,
                    message: `请选择第${index + 1}条内容对应日期`,
                    trigger: 'change',
                  },
                ]"
              >
                <el-date-picker
                  v-model="item.periodTime"
                  type="date"
                  value-format="yyyy-MM-dd"
                  :placeholder="`请选择第${index + 1}个内容对应日期`"
                  style="width: 100%"
                />
              </el-form-item>
            </el-col>
            <!-- 内容输入 -->
            <el-col :span="12">
              <el-form-item
                :label="`内容${index + 1}`"
                :prop="`contents[${index}].content`"
                :rules="[
                  {
                    required: true,
                    message: `请填写第${index + 1}条内容的详情`,
                    trigger: 'blur',
                  },
                ]"
              >
                <el-input
                  v-model="item.content"
                  type="textarea"
                  :rows="5"
                  placeholder="请输入工作内容详情"
                />
              </el-form-item>
            </el-col>
            <!-- 增删按钮 -->
            <el-col :span="4" class="text-center pt20">
              <el-button
                type="text"
                @click="addWorkContent"
                v-if="index === 0 && editWorkInfo.workCycle === '2002'"
              >
                添加内容
              </el-button>
              <el-button
                type="danger"
                @click="deleteWorkContent(item)"
                v-else-if="editWorkInfo.workCycle === '2002'"
              >
                删除内容
              </el-button>
            </el-col>
          </el-row>
        </el-row>

        <!-- 优化：新增保存草稿按钮，调整按钮布局 -->
        <el-row>
          <el-col :span="24" class="text-right">
            <el-button type="warning" @click="goBack" icon="el-icon-close">
              返回
            </el-button>
            <el-button
              type="info"
              @click="saveDraft"
              :loading="submitLoading"
              icon="el-icon-save"
              style="margin-left: 10px"
            >
              保存草稿
            </el-button>
            <el-button
              type="primary"
              @click="submitForm"
              :loading="submitLoading"
              icon="el-icon-check"
              style="margin-left: 10px"
            >
              提交
            </el-button>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 园区选择弹窗 -->
    <el-dialog
      title="选择园区"
      :visible.sync="parkDialogVisible"
      width="500px"
      :close-on-click-modal="false"
      class="park-select-dialog"
      @close="resetParkState"
    >
      <!-- 园区搜索 -->
      <el-input
        v-model="parkSearchName"
        placeholder="输入园区名称搜索"
        prefix-icon="el-icon-search"
        style="margin-bottom: 15px"
        @input="handleParkSearch"
        clearable
      />
      <!-- 园区列表 -->
      <el-table
        v-loading="parkLoading"
        :data="filteredParkList"
        border
        style="width: 100%"
        @row-click="handleParkSelect"
        size="small"
        highlight-current-row
      >
        <el-table-column
          prop="name"
          label="园区名称"
          align="center"
          min-width="200"
        />
        <el-table-column label="园区编码" align="center" min-width="150">
          <template slot-scope="scope">
            {{ getCommunityCode(scope.row) || "无" }}
          </template>
        </el-table-column>
        <el-table-column
          prop="storeName"
          label="所属物业"
          align="center"
          min-width="150"
        />
      </el-table>
      <!-- 分页 -->
      <el-pagination
        @size-change="handleParkSizeChange"
        @current-change="handleParkPageChange"
        :current-page="parkPage.current"
        :page-sizes="[5, 10, 20]"
        :page-size="parkPage.size"
        layout="total, sizes, prev, pager, next, jumper"
        :total="parkPage.total"
        style="margin-top: 15px; text-align: right"
        background
      />
    </el-dialog>

    <!-- 人员选择组件 -->
    <select-staff
      ref="selectStaff"
      @selectStaff="selectStaff"
      :community-id="selectedCommunityId"
      :key="`staff-select-${selectedCommunityId}-${currentSelectType}`"
    />

    <!-- 新增：附件上传弹窗 -->
    <el-dialog
      title="上传合同附件"
      :visible.sync="uploadModalVisible"
      width="500px"
      destroy-on-close
      :before-close="handleDialogClose"
    >
      <el-upload
        class="upload-demo"
        drag
        action="/callComponent/upload/uploadFile/uploadImage"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        :limit="1"
        name="uploadFile"
        :data="uploadExtraParams"
        :file-list="fileListUpload"
        :on-success="handleUploadSuccess"
        :on-remove="handleRemove"
        :auto-upload="true"
        :on-exceed="handleExceed"
        :on-error="handleUploadError"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
        <div class="el-upload__tip" slot="tip">
          支持 PDF, Word, Excel, PPT (单个文件最大 10MB)
        </div>
      </el-upload>
      <div slot="footer" class="dialog-footer">
        <el-button @click="uploadModalVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="handleConfirmUpload"
          :loading="uploadConfirmLoading"
        >
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  updateWorkPool,
  listWorkType,
  queryStartWork,
  listWorkTask,
  listWorkCopy,
  listWorkCycle,
} from "@/api/oa/editWorkApi";
import { getCommunityId, listCommunitys } from "@/api/community/communityApi";
// 引入文件上传接口（复用文件管理的上传接口）
import { uploadFile } from "@/api/contract/fileManageApi";
import SelectStaff from "@/components/staff/SelectStaff";
import { getUuid } from "@/utils/commonUtil";

export default {
  name: "EditWorkList",
  components: {
    SelectStaff,
  },
  data() {
    return {
      chooseStaffFlag: "1",
      submitLoading: false,
      parkDialogVisible: false,
      parkLoading: false,
      parkSearchName: "",
      parkList: [],
      parkPage: { current: 1, size: 10, total: 0 },
      selectedCommunityId: "",
      selectedCommunityName: "",
      currentSelectType: "",
      // 级联选择器配置（核心新增）
      cascaderProps: {
        value: "wtId",
        label: "typeName",
        children: "children",
        checkStrictly: true,
        expandTrigger: "click",
        lazy: false,
        emitPath: true,
      },
      workTypeOptions: [], // 级联选择器的树形数据
      // 替换为真实的文件服务器基础路径（必填）
      fileBaseUrl: "https://你的文件服务器域名/", // 示例：http://192.168.1.100:8080/
      editWorkInfo: {
        workId: "",
        workName: "",
        wtIds: [], // 级联选择器的绑定值（数组）
        wtId: "", // 兼容原有提交逻辑的单个ID
        workCycle: "1001",
        urgencyLevel: "",
        staffs: [],
        copyStaffs: [],
        pathUrl: "", // 单字符串存储附件相对路径
        contents: [],
        period: "",
        months: [],
        days: [],
        workdays: [],
        hours: "24",
        communityId: "",
        problemsEncountered: "",
        workPlan: "",
        state: 1,
      },
      weekDays: [
        "星期一",
        "星期二",
        "星期三",
        "星期四",
        "星期五",
        "星期六",
        "星期日",
      ],
      // 新增：上传弹窗相关（对齐文件管理页面逻辑）
      uploadModalVisible: false,
      uploadExtraParams: {
        communityId: "", // 园区ID
        storeId: "", // 上传时携带的商户ID
        type: "work_attachment", // 文件类型标识
      },
      fileListUpload: [], // 上传组件的文件列表
      uploadFileInfo: {}, // 存储上传成功后的完整文件信息
      currentUploadFileName: "", // 存储当前上传文件的原始名称
      currentUploadFileSize: 0, // 存储当前上传文件的大小
      uploadConfirmLoading: false,
    };
  },
  computed: {
    filteredParkList() {
      if (!this.parkSearchName) return this.parkList;
      return this.parkList.filter((item) =>
        item.name.toLowerCase().includes(this.parkSearchName.toLowerCase()),
      );
    },
  },
  created() {
    this.editWorkInfo.workId = this.$route.query.workId;
    this.editWorkInfo.communityId = getCommunityId();
    // 初始化上传参数（对齐文件管理页面）
    this.uploadExtraParams.communityId = this.getCommunityId();
    this.loadData();
  },
  methods: {
    // 获取园区ID（对齐文件管理页面逻辑）
    getCommunityId() {
      return (
        localStorage.getItem("communityId") ||
        this.editWorkInfo.communityId ||
        ""
      );
    },

    // 根据文件名获取文件类型（复用文件管理页面方法）
    getFileTypeByFileName(fileName) {
      if (!fileName) return "other";
      const ext = fileName.split(".").pop().toLowerCase();
      switch (ext) {
        case "doc":
        case "docx":
          return "doc";
        case "xls":
        case "xlsx":
          return "xls";
        case "ppt":
        case "pptx":
          return "ppt";
        case "pdf":
          return "pdf";
        case "jpg":
        case "png":
        case "jpeg":
          return "img";
        default:
          return "other";
      }
    },

    // 核心：提取纯文件名+后缀（处理URL解码、路径截取、参数过滤）
    getFileNameFromUrl(url) {
      try {
        // 解码URL中的特殊字符（如中文、空格、%20等）
        const decodedUrl = decodeURIComponent(url);
        // 按/分割路径，取最后一段
        const parts = decodedUrl.split("/");
        let fileName = parts.pop() || "未命名文件";
        // 过滤URL参数（如?token=xxx、#hash等）
        fileName = fileName.split("?")[0].split("#")[0];
        return fileName;
      } catch (e) {
        console.error("提取文件名失败:", e);
        return "未命名文件";
      }
    },

    // 新增：打开上传弹窗（对齐文件管理页面逻辑）
    openUploadModal() {
      // 重置上传相关数据
      this.fileListUpload = [];
      this.uploadFileInfo = {};
      this.currentUploadFileName = "";
      this.currentUploadFileSize = 0;
      this.uploadModalVisible = true;
    },

    // 新增：上传成功回调（完全对齐文件管理页面逻辑）
    handleUploadSuccess(response, file) {
      try {
        // 保存上传文件的基础信息
        this.currentUploadFileName = file.name;
        this.currentUploadFileSize = file.size;

        // 解析上传接口返回的数据（适配文件上传接口返回格式）
        let resData = response;
        if (typeof response === "string") {
          resData = JSON.parse(response);
        }

        if (resData.url && resData.fileId) {
          // 组装java110框架要求的文件信息参数
          this.uploadFileInfo = {
            fileName: file.name, // 文件名称
            fileType: this.getFileTypeByFileName(file.name), // 文件类型
            fileUrl: resData.url, // 文件地址(完整URL)
            fileId: resData.fileId, // 文件存储ID（OSS等存储的ID）
            fileSize: file.size.toString(), // 文件大小(字节)，转字符串适配java110框架
            communityId: this.uploadExtraParams.communityId,
          };

          this.$message.success("文件上传成功");
        } else {
          throw new Error("上传返回数据不完整，缺少url或fileId");
        }
      } catch (error) {
        console.error("解析上传结果失败：", error);
        this.$message.error("文件上传成功，但解析返回数据失败");
        this.fileListUpload = [];
      }
    },

    // 新增：移除已上传文件（对齐文件管理页面逻辑）
    handleRemove(file, fileList) {
      this.uploadFileInfo = {};
      this.currentUploadFileName = "";
      this.currentUploadFileSize = 0;
      this.fileListUpload = fileList;
    },

    // 新增：文件数量超限（对齐文件管理页面逻辑）
    handleExceed(files, fileList) {
      this.$message.warning(
        `当前限制上传1个文件，你选择了${files.length + fileList.length}个文件`,
      );
    },

    // 新增：上传失败（对齐文件管理页面逻辑）
    handleUploadError(error) {
      console.error("文件上传失败:", error);
      this.$message.error("文件上传失败，请重试");
      this.fileListUpload = [];
      this.uploadFileInfo = {};
    },

    // 新增：确认上传（弹窗确定按钮，调用java110保存接口）
    async handleConfirmUpload() {
      try {
        // 验证数据
        if (Object.keys(this.uploadFileInfo).length === 0) {
          this.$message.warning("请先上传文件后再点击确定");
          return;
        }

        this.uploadConfirmLoading = true;

        // 第一步：调用文件管理的上传接口保存文件信息
        const res = await uploadFile(this.uploadFileInfo);

        if (res.code === 0) {
          // 第二步：将文件URL赋值给工作单表单
          this.editWorkInfo.pathUrl = this.uploadFileInfo.fileUrl;
          this.$message.success("附件上传成功");
          this.uploadModalVisible = false;
          // 重置上传数据
          this.uploadFileInfo = {};
          this.currentUploadFileName = "";
          this.currentUploadFileSize = 0;
        } else {
          this.$message.error(res.msg || "附件信息保存失败");
        }
      } catch (error) {
        console.error("确认上传失败:", error);
        this.$message.error("附件上传异常，请重试");
      } finally {
        this.uploadConfirmLoading = false;
      }
    },

    // 新增：关闭上传弹窗前的处理（对齐文件管理页面）
    handleDialogClose() {
      this.fileListUpload = [];
      this.uploadFileInfo = {};
      this.currentUploadFileName = "";
      this.currentUploadFileSize = 0;
      this.uploadConfirmLoading = false;
    },

    // 新增：移除现有附件
    removeFile() {
      this.editWorkInfo.pathUrl = "";
      this.$message.success("附件已移除");
    },

    resetParkState() {
      this.selectedCommunityId = "";
      this.selectedCommunityName = "";
      this.parkSearchName = "";
      this.parkPage.current = 1;
    },

    getCommunityCode(row) {
      if (!row.communityAttrDtos || !Array.isArray(row.communityAttrDtos)) {
        return "";
      }
      const codeItem = row.communityAttrDtos.find(
        (item) => item.specName === "社区编码",
      );
      return codeItem.value || "";
    },

    openParkSelect(type) {
      this.resetParkState();
      this.currentSelectType = type;
      this.chooseStaffFlag = type === "processor" ? "1" : "2";
      this.getParkList();
      this.parkDialogVisible = true;
    },

    async getParkList() {
      this.parkLoading = true;
      try {
        const params = { page: this.parkPage.current, row: this.parkPage.size };
        const res = await listCommunitys(params);
        this.parkList = res.data || [];
        this.parkPage.total = res.total || 0;
      } catch (error) {
        console.error("获取园区列表失败:", error);
        this.$message.warning("获取园区列表失败");
      } finally {
        this.parkLoading = false;
      }
    },

    handleParkSearch() {
      this.parkPage.current = 1;
      this.getParkList();
    },

    handleParkSizeChange(size) {
      this.parkPage.size = size;
      this.getParkList();
    },

    handleParkPageChange(current) {
      this.parkPage.current = current;
      this.getParkList();
    },

    handleParkSelect(row) {
      let communityId = row.communityId;
      if (!communityId) {
        const idItem = row.communityAttrDtos.find(
          (item) => item.specName === "社区编码",
        );
        if (idItem) communityId = idItem.value;
      }
      if (!communityId) {
        this.$message.error("未找到有效的园区 ID，请联系管理员");
        return;
      }

      this.selectedCommunityId = communityId;
      this.selectedCommunityName = row.name;
      this.parkDialogVisible = false;

      this.$nextTick(() => {
        if (this.$refs.selectStaff) {
          this.$refs.selectStaff.open(communityId);
        }
      });
    },

    // 核心新增：根据选中的wtId生成级联选择器的完整路径
    generateCascaderPath(targetWtId, treeData, path = []) {
      for (const node of treeData) {
        const newPath = [...path, node.wtId];
        if (node.wtId === targetWtId) {
          return newPath;
        }
        if (node.children && node.children.length > 0) {
          const foundPath = this.generateCascaderPath(
            targetWtId,
            node.children,
            newPath,
          );
          if (foundPath) {
            return foundPath;
          }
        }
      }
      return null;
    },

    async loadData() {
      try {
        // 先加载工作类型树形数据
        await this._listWorkTypes();
        // 再加载工作单详情
        await this._loadWorkPool();
        // 加载其他数据
        await Promise.all([
          this._loadTaskStaff(),
          this._loadWorkCopy(),
          this._loadWorkCycle(),
        ]);

        // 核心：回显级联选择器（根据wtId生成完整路径）
        if (this.editWorkInfo.wtId && this.workTypeOptions.length > 0) {
          const cascaderPath = this.generateCascaderPath(
            this.editWorkInfo.wtId,
            this.workTypeOptions,
          );
          if (cascaderPath) {
            this.editWorkInfo.wtIds = cascaderPath;
          }
        }

        this.handleWorkCycleChange(this.editWorkInfo.workCycle);

        // 确保 pathUrl 是字符串，清理无效值
        this.editWorkInfo.pathUrl = String(
          this.editWorkInfo.pathUrl || "",
        ).trim();

        // 强制刷新组件（解决数据加载后未渲染的问题）
        this.$nextTick(() => {
          this.$forceUpdate();
        });
      } catch (error) {
        console.error("加载数据失败:", error);
        this.$message.error("加载数据失败");
      }
    },

    // 级联选择器变更事件（核心新增）
    handleCascaderChange(val) {
      console.log("🚀 级联选择结果:", val);
      this.editWorkInfo.wtIds = val;
      // 取最后一个ID作为提交的wtId，兼容原有逻辑
      this.editWorkInfo.wtId = val.length > 0 ? val[val.length - 1] : "";
    },

    editWorkValidate(isDraft = false) {
      return new Promise((resolve, reject) => {
        if (!this.$refs.form) {
          reject(new Error("表单实例不存在"));
          return;
        }

        if (isDraft) {
          let draftValid = true;
          if (!this.editWorkInfo.workName.trim()) {
            this.$message.warning("请输入工作名称");
            draftValid = false;
          }
          if (!this.editWorkInfo.wtIds.length) {
            this.$message.warning("请选择工作类型");
            draftValid = false;
          }
          if (!this.editWorkInfo.urgencyLevel) {
            this.$message.warning("请选择紧急程度");
            draftValid = false;
          }
          resolve(draftValid);
          return;
        }

        this.$refs.form.validate((valid) => {
          if (valid) {
            if (!this.editWorkInfo.wtIds.length) {
              this.$message.warning("请选择工作类型");
              resolve(false);
              return;
            }
            let contentValid = true;
            this.editWorkInfo.contents.forEach((item, index) => {
              if (!item.content.trim()) {
                this.$message.warning(`请填写第${index + 1}条内容的详情`);
                contentValid = false;
              }
              if (!item.periodTime) {
                this.$message.warning(`请选择第${index + 1}条内容对应日期`);
                contentValid = false;
              }
            });
            resolve(contentValid);
          } else {
            resolve(false);
          }
        });
      });
    },

    async handleWorkSubmit(isDraft = false) {
      if (this.submitLoading) return;
      this.submitLoading = true;

      try {
        const valid = await this.editWorkValidate(isDraft);
        if (!valid) return;

        const params = {
          ...this.editWorkInfo,
          isDraft: isDraft ? 1 : 0,
          contents: this.editWorkInfo.contents.map((item) => ({
            ...item,
            wtId: this.editWorkInfo.wtId, // 仍使用单个wtId提交
          })),
          months: this.editWorkInfo.months,
          days: this.editWorkInfo.days,
          workdays: this.editWorkInfo.workdays,
          state: this.editWorkInfo.state,
          pathUrl: this.editWorkInfo.pathUrl.trim() || "",
        };

        const res = await updateWorkPool(params);
        const successMsg = isDraft ? "草稿保存成功" : "提交成功";
        const failMsg = isDraft ? "草稿保存失败" : "提交失败";

        if (res.code === 0) {
          this.$message.success(successMsg);
          this.goBack();
        } else {
          this.$message.error(res.msg || failMsg);
        }
      } catch (error) {
        console.error(isDraft ? "保存草稿失败:" : "提交工单失败:", error);
        this.$message.error(isDraft ? "草稿保存异常" : "提交异常");
      } finally {
        this.submitLoading = false;
      }
    },

    async submitForm() {
      await this.handleWorkSubmit(false);
    },

    async saveDraft() {
      await this.handleWorkSubmit(true);
    },

    // 修改：加载树形工作类型数据（替换原有扁平数据）
    async _listWorkTypes() {
      try {
        const params = {
          page: 1,
          row: 100,
          communityId: this.editWorkInfo.communityId,
        };
        const res = await listWorkType(params);
        // 直接使用树形数据
        this.workTypeOptions = res.data || [];
        console.log("📋 编辑页面工作类型树形数据:", this.workTypeOptions);
      } catch (error) {
        console.error("获取工作单类型失败:", error);
        this.$message.warning("获取工作单类型失败");
      }
    },

    selectStaff(staff) {
      if (!staff || !staff.userId || !staff.userName) return;

      const staffData = {
        staffId: staff.userId,
        staffName: staff.userName,
        communityId: this.selectedCommunityId,
        communityName: this.selectedCommunityName || "未知园区",
      };

      if (this.chooseStaffFlag === "1") {
        const isExist = this.editWorkInfo.staffs.some(
          (item) =>
            item.staffId === staff.userId &&
            item.communityId === this.selectedCommunityId,
        );
        if (!isExist) {
          this.editWorkInfo.staffs.push(staffData);
        }
      } else {
        const isExist = this.editWorkInfo.copyStaffs.some(
          (item) =>
            item.staffId === staff.userId &&
            item.communityId === this.selectedCommunityId,
        );
        if (!isExist) {
          this.editWorkInfo.copyStaffs.push(staffData);
        }
      }
    },

    _deleteWorkStaff(staff) {
      if (!staff) return;
      this.editWorkInfo.staffs = this.editWorkInfo.staffs.filter(
        (item) =>
          !(
            item.staffId === staff.staffId &&
            item.communityId === staff.communityId
          ),
      );
    },

    _deleteCopyWorkStaff(staff) {
      if (!staff) return;
      this.editWorkInfo.copyStaffs = this.editWorkInfo.copyStaffs.filter(
        (item) =>
          !(
            item.staffId === staff.staffId &&
            item.communityId === staff.communityId
          ),
      );
    },

    async _loadWorkPool() {
      const params = {
        page: 1,
        row: 1,
        workId: this.editWorkInfo.workId,
      };
      const { data } = await queryStartWork(params);
      if (data && data.length > 0) {
        const workData = data[0];
        Object.assign(this.editWorkInfo, {
          workName: workData.workName,
          urgencyLevel: workData.urgencyLevel,
          wtId: workData.wtId || "", // 保留原有单个ID
          workCycle: workData.workCycle || "1001",
          pathUrl: workData.pathUrls || "", // 接口返回的附件路径字段是pathUrls
          state: workData.state || 1,
        });

        this.editWorkInfo.contents = workData.contents
          ? workData.contents.map((c) => ({
              id: c.contentId || getUuid(), // 用contentId作为唯一标识
              content: c.content || "",
              periodTime: c.periodTime ? c.periodTime.split(" ")[0] : "",
            }))
          : [
              {
                id: getUuid(),
                content: "",
                periodTime: "",
              },
            ];
      }
    },

    async _loadTaskStaff() {
      const params = {
        page: 1,
        row: 100,
        workId: this.editWorkInfo.workId,
      };
      const { data } = await listWorkTask(params);
      this.editWorkInfo.staffs = data
        ? data.map((item) => ({
            staffId: item.staffId,
            staffName: item.staffName,
            communityId: item.communityId || this.editWorkInfo.communityId,
            communityName: item.communityName || this.editWorkInfo.communityId,
          }))
        : [];
    },

    async _loadWorkCopy() {
      const params = {
        page: 1,
        row: 100,
        workId: this.editWorkInfo.workId,
      };
      const { data } = await listWorkCopy(params);
      this.editWorkInfo.copyStaffs = data
        ? data.map((item) => ({
            staffId: item.staffId,
            staffName: item.staffName,
            communityId: item.communityId || this.editWorkInfo.communityId,
            communityName: item.communityName || this.editWorkInfo.communityId,
          }))
        : [];
    },

    async _loadWorkCycle() {
      const params = {
        page: 1,
        row: 1,
        workId: this.editWorkInfo.workId,
      };
      const { data } = await listWorkCycle(params);
      if (data && data.length > 0) {
        this.editWorkInfo.period = data[0].period;
        this.editWorkInfo.hours = data[0].hours || "24";
        if (data[0].periodMonth) {
          this.editWorkInfo.months = data[0].periodMonth
            .split(",")
            .map(Number)
            .filter((item) => !isNaN(item));
        }
        if (data[0].periodDay) {
          this.editWorkInfo.days = data[0].periodDay
            .split(",")
            .map(Number)
            .filter((item) => !isNaN(item));
        }
        if (data[0].workdays) {
          this.editWorkInfo.workdays = data[0].workdays
            .split(",")
            .map(Number)
            .filter((item) => !isNaN(item));
        }
      }
    },

    addWorkContent() {
      this.editWorkInfo.contents.push({
        id: getUuid(),
        content: "",
        periodTime: "",
      });
    },

    deleteWorkContent(content) {
      if (this.editWorkInfo.contents.length <= 1) {
        this.$message.warning("至少保留1条工作内容");
        return;
      }
      this.editWorkInfo.contents = this.editWorkInfo.contents.filter(
        (item) => item.id !== content.id,
      );
    },

    handleWorkCycleChange(val) {
      if (val === "1001") {
        this.editWorkInfo.period = "";
        this.editWorkInfo.months = [];
        this.editWorkInfo.days = [];
        this.editWorkInfo.workdays = [];
        if (this.editWorkInfo.contents.length > 1) {
          this.editWorkInfo.contents = this.editWorkInfo.contents.slice(0, 1);
        }
      } else if (val === "2002") {
        this.editWorkInfo.contents.forEach((item) => {
          if (item.periodTime === undefined) item.periodTime = "";
        });
      }
    },

    goBack() {
      this.$router.go(-1);
    },
  },
};
</script>

<style lang="scss" scoped>
.edit-work-container {
  padding: 20px;

  .staff-tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    min-height: 36px;
    .el-tag {
      margin-right: 10px;
      margin-bottom: 5px;
    }
  }

  .work-content-group {
    margin-bottom: 30px;
    padding-bottom: 20px;
    border-bottom: 1px solid #f5f5f5;
  }

  .content-operate-row {
    margin-bottom: 10px;
  }

  .pt20 {
    padding-top: 20px;
  }

  // 附件信息区域样式优化
  .file-info {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    .el-tag {
      max-width: 200px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .park-select-dialog {
    .el-dialog__body {
      padding: 15px 20px;
    }
    .el-table {
      --el-table-row-hover-bg-color: #f8fafc;
      --el-table-current-row-bg-color: #e6f7ff;
    }
    .el-pagination {
      --el-pagination-button-active-bg-color: #409eff;
    }
  }

  // 上传弹窗样式（对齐文件管理页面）
  ::v-deep .upload-demo {
    margin: 20px 0;
    .el-upload-dragger {
      width: 100%;
      height: 200px;
      line-height: 200px;
    }
    .el-upload__text {
      font-size: 14px;
    }
  }

  // 级联选择器样式优化
  ::v-deep .el-cascader {
    width: 100%;
  }
  ::v-deep .el-cascader-node__label {
    padding: 4px 8px;
  }
  ::v-deep .el-cascader-menu {
    max-height: 300px;
    min-width: 200px;
  }
  ::v-deep .el-cascader-menu__wrap {
    overflow-y: auto !important;
  }
  ::v-deep .el-cascader-node {
    height: auto !important;
    line-height: 24px !important;
    padding: 2px 0 !important;
  }
}
</style>
